/**
 * GET /admin/segments — liste paginée + filtres serveur (loyalty/SEGMENTS_FRONTEND.md).
 */

export const DEFAULT_SEGMENT_LIST_FILTERS = {
    search: "",
    activeFilter: "all",
    kindFilter: "all",
    providerFilter: "all",
};

/** Valeur UI ListToolbar → sort_by / sort_order (tri global catalogue). */
export const DEFAULT_SEGMENT_SORT = "name_asc";

export const SEGMENT_SORT_OPTIONS = [
    { value: "name_asc", label: "Nom (A-Z)" },
    { value: "name_desc", label: "Nom (Z-A)" },
    { value: "members_desc", label: "Membres (décroissant)" },
    { value: "members_asc", label: "Membres (croissant)" },
    { value: "updated_desc", label: "Mise à jour (récent)" },
    { value: "created_desc", label: "Création (récent)" },
    { value: "last_computed_desc", label: "Dernier calcul (récent)" },
];

const UI_SORT_TO_API = {
    name_asc: { sort_by: "name", sort_order: "asc" },
    name_desc: { sort_by: "name", sort_order: "desc" },
    members_desc: { sort_by: "member_count", sort_order: "desc" },
    members_asc: { sort_by: "member_count", sort_order: "asc" },
    updated_desc: { sort_by: "updated_at", sort_order: "desc" },
    created_desc: { sort_by: "created_at", sort_order: "desc" },
    last_computed_desc: { sort_by: "last_computed_at", sort_order: "desc" },
};

/**
 * Convertit l'état UI ListToolbar → query params API.
 * @returns {Record<string, string>}
 */
export function uiFiltersToQueryParams({ search, activeFilter, kindFilter, providerFilter }) {
    const params = {};
    const q = (search || "").trim();
    if (q) params.q = q;
    if (activeFilter === "active") params.active = "true";
    if (activeFilter === "inactive") params.active = "false";
    if (kindFilter === "dynamic") params.is_dynamic = "true";
    if (kindFilter === "static") params.is_dynamic = "false";
    if (providerFilter === "INTERNAL" || providerFilter === "UNOMI") {
        params.provider = providerFilter;
    }
    return params;
}

export function uiSortToQueryParams(sortKey) {
    const mapped = UI_SORT_TO_API[sortKey] || UI_SORT_TO_API[DEFAULT_SEGMENT_SORT];
    return {
        sort_by: mapped.sort_by,
        sort_order: mapped.sort_order,
    };
}

/** Filtres + tri pour GET /admin/segments (répéter sur chaque page). */
export function buildSegmentListQueryParams({ search, activeFilter, kindFilter, providerFilter, sortBy }) {
    return {
        ...uiFiltersToQueryParams({ search, activeFilter, kindFilter, providerFilter }),
        ...uiSortToQueryParams(sortBy),
    };
}

export function hasActiveSegmentListFilters(queryParams) {
    const filterKeys = ["q", "active", "is_dynamic", "provider"];
    return filterKeys.some((k) => queryParams?.[k] != null && String(queryParams[k]).trim() !== "");
}

/**
 * Parse GET /admin/segments — contrat paginé (SEGMENTS_FRONTEND.md).
 * items = une page (≤ limit) ; total = count global filtré.
 */
export function parseSegmentsListResponse(data, requestedLimit, requestedOffset = 0) {
    if (Array.isArray(data)) {
        // Ancien format tableau (fallback uniquement)
        const limit = requestedLimit > 0 ? requestedLimit : data.length;
        const offset = requestedOffset;
        return {
            items: data.slice(offset, offset + limit),
            total: data.length,
            limit,
            offset,
            filters: null,
            sort: null,
        };
    }

    const items = Array.isArray(data?.items) ? data.items : [];
    return {
        items,
        total: Number.isFinite(data?.total) ? data.total : items.length,
        limit: Number.isFinite(data?.limit) ? data.limit : requestedLimit,
        offset: Number.isFinite(data?.offset) ? data.offset : requestedOffset,
        filters: data?.filters ?? null,
        sort: data?.sort ?? null,
    };
}

/**
 * @param {object} opts
 * @param {string} opts.brandName
 * @param {number} opts.limit
 * @param {number} opts.offset
 * @param {boolean} [opts.forceSync]
 * @param {Record<string, string>} [opts.queryParams] - filtres + sort_by, sort_order
 */
export function buildSegmentsListUrl({
    brandName,
    limit,
    offset,
    forceSync = false,
    queryParams = {},
}) {
    const params = new URLSearchParams({
        brand: brandName,
        limit: String(limit),
        offset: String(offset),
    });
    Object.entries(queryParams).forEach(([key, value]) => {
        if (value != null && String(value).trim() !== "") {
            params.set(key, String(value));
        }
    });
    if (forceSync && offset === 0) {
        params.set("sync_unomi", "true");
    }
    return `/admin/segments?${params}`;
}
